type ChunkLoader = () => Promise<unknown>;

/** Lazy import fns — same modules as lazyPages.ts */
const CHUNKS = {
  jobseekerDashboard: () => import('../../features/job-seeker/pages/jobseekerDashboard'),
  referralDashboard: () => import('../../features/referrer/pages/referralDashboard'),
  resumeReview: () => import('../../features/job-seeker/pages/ResumeReview'),
  cultureCall: () => import('../../features/job-seeker/pages/CultureCall'),
  referralSession: () => import('../../features/job-seeker/pages/ReferralSession'),
  mySessions: () => import('../../features/bookings/pages/MySessions'),
  profile: () => import('../../features/profile/pages/ProfilePage'),
  jobOpenings: () => import('../../features/job-seeker/pages/JobOpenings'),
  jobDetailView: () => import('../../features/job-seeker/pages/JobDetailView'),
  invoices: () => import('../../features/bookings/pages/Invoices'),
  services: () => import('../../features/referrer/pages/Services'),
  analytics: () => import('../../features/referrer/pages/DashbordAnalytics'),
  calendar: () => import('../../features/referrer/pages/Calender'),
  setting: () => import('../../features/settings/pages/setting'),
  postJob: () => import('../../features/referrer/pages/PostJob'),
  referrerResumeReview: () => import('../../features/referrer/pages/ReferrerResumeReview'),
  payoutTransfer: () => import('../../features/referrer/pages/PayoutTransfer'),
  payouts: () => import('../../features/referrer/pages/Payouts'),
  support: () => import('../../features/support/pages/Support'),
  bookingConfirmed: () => import('../../features/bookings/pages/BookingConfirmed'),
  rateReview: () => import('../../features/bookings/pages/RateReview'),
  rewards: () => import('../../features/misc/pages/Rewards'),
  categories: () => import('../../features/misc/pages/Categories'),
  notifications: () => import('../../features/misc/pages/Notifications'),
  jobDetails: () => import('../../features/misc/pages/JobDetails'),
  adminUpgrades: () => import('../../features/admin/pages/AdminReferrerUpgrades'),
} as const satisfies Record<string, ChunkLoader>;

const PATH_LOADERS: Array<{ test: (path: string) => boolean; loaders: ChunkLoader[] }> = [
  {
    test: (p) => p.includes('/resume-review') || p.includes('/culture-call'),
    loaders: [CHUNKS.resumeReview, CHUNKS.cultureCall],
  },
  { test: (p) => p.includes('/referral-session'), loaders: [CHUNKS.referralSession] },
  { test: (p) => p.includes('/my-sessions'), loaders: [CHUNKS.mySessions] },
  { test: (p) => p.includes('/profile'), loaders: [CHUNKS.profile] },
  { test: (p) => p.includes('/jobopenings') || p.includes('/job-opening'), loaders: [CHUNKS.jobOpenings, CHUNKS.jobDetailView] },
  { test: (p) => p.includes('/invoices'), loaders: [CHUNKS.invoices] },
  { test: (p) => p.startsWith('/settings'), loaders: [CHUNKS.setting] },
  { test: (p) => p.includes('/services'), loaders: [CHUNKS.services] },
  { test: (p) => p.includes('/analytics'), loaders: [CHUNKS.analytics, CHUNKS.payoutTransfer] },
  { test: (p) => p.includes('/calendar'), loaders: [CHUNKS.calendar] },
  { test: (p) => p.includes('/post-job'), loaders: [CHUNKS.postJob] },
  { test: (p) => p.includes('/payouts'), loaders: [CHUNKS.payouts] },
  { test: (p) => p === '/support' || p.includes('/support'), loaders: [CHUNKS.support] },
  { test: (p) => p.includes('/booking-confirmed'), loaders: [CHUNKS.bookingConfirmed] },
  { test: (p) => p.includes('/rate-review'), loaders: [CHUNKS.rateReview] },
  { test: (p) => p === '/rewards', loaders: [CHUNKS.rewards] },
  { test: (p) => p === '/categories', loaders: [CHUNKS.categories] },
  { test: (p) => p === '/notifications', loaders: [CHUNKS.notifications] },
  { test: (p) => p.startsWith('/job/'), loaders: [CHUNKS.jobDetails] },
  { test: (p) => p.includes('/admin/referrer-upgrades'), loaders: [CHUNKS.adminUpgrades] },
  {
    test: (p) => p === '/referral-dashboard' || p.startsWith('/referral-dashboard/'),
    loaders: [CHUNKS.referralDashboard],
  },
  {
    test: (p) => p === '/jobseeker-dashboard' || p.startsWith('/jobseeker-dashboard/'),
    loaders: [CHUNKS.jobseekerDashboard],
  },
];

const prefetched = new Set<ChunkLoader>();

function runPrefetch(loaders: ChunkLoader[]) {
  for (const load of loaders) {
    if (prefetched.has(load)) continue;
    prefetched.add(load);
    void load();
  }
}

/** Warm all dashboard route chunks after the shell mounts (idle time). */
export function prefetchAllDashboardChunks() {
  const all = Object.values(CHUNKS);
  const schedule =
    typeof requestIdleCallback === 'function'
      ? (cb: () => void) => requestIdleCallback(cb, { timeout: 4000 })
      : (cb: () => void) => window.setTimeout(cb, 200);

  schedule(() => runPrefetch(all));
}

/** Prefetch a single nav target (hover/focus) so navigation rarely suspends. */
export function prefetchDashboardPath(pathname: string) {
  const path = pathname.replace(/\/+$/, '') || '/';
  const matched = PATH_LOADERS.filter(({ test }) => test(path));
  const loaders = matched.flatMap((m) => m.loaders);
  if (loaders.length > 0) {
    runPrefetch(loaders);
    return;
  }
  if (path.startsWith('/referral-dashboard')) {
    runPrefetch([CHUNKS.referralDashboard]);
  } else if (path.startsWith('/jobseeker-dashboard')) {
    runPrefetch([CHUNKS.jobseekerDashboard]);
  }
}
